import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "react-phone-number-input/style.css";
import App from "./App.tsx";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import SignIn from "./app/auth/sign-in..tsx";
import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import SignUp from "./app/auth/sign-up.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import StepOne from "./app/onboarding/step-1.tsx";
import { Toaster } from "./components/ui/sonner.tsx";
import StepTwo from "./app/onboarding/step-2.tsx";
import StepThree from "./app/onboarding/step-3.tsx";
import AuthLayout from "./components/layout/auth-layout/auth-layout.tsx";
import AuthRest from "./components/layout/auth-layout/auth-rest.tsx";
import OnboardingLayout from "./components/layout/onboarding-layout/onboarding-layout.tsx";
import OnboardingRest from "./components/layout/onboarding-layout/onboarding-rest.tsx";
import AppLayout from "./components/layout/app-layout/app-layout.tsx";
import Dashboard from "./app/app/dashboard.tsx";
import Conversations from "./app/app/conversations.tsx";
import Leads from "./app/app/leads.tsx";
import ProactiveLayout from "./components/proactive-chats/proactive-layout.tsx";
import EasySetup from "./app/app/proactive-chats/easy-setup.tsx";
import AdvancedSetup from "./app/app/proactive-chats/advanced-setup.tsx";
import TrainingLayout from "./components/ai-training/training-layout.tsx";
import KnowledgeBase from "./app/app/ai-training/knowledge-base.tsx";
import ActiveTrainings from "./app/app/ai-training/active-training.tsx";
import TrainingHistory from "./app/app/ai-training/training-history.tsx";
import Corrections from "./app/app/ai-training/corrections.tsx";
import CustomInstructions from "./app/app/ai-training/custom-instructions.tsx";
import ChatSettingsLayout from "./components/chat-settings/chat-settings-layout.tsx";
import LookAndFeel from "./app/app/chat-settings/look-and-feel.tsx";
import PredefinedQuestions from "./app/app/chat-settings/predefined-questions.tsx";
import Configuration from "./app/app/chat-settings/configuration.tsx";
import Integration from "./app/app/chat-settings/integration.tsx";
import Installation from "./app/app/chat-settings/installation.tsx";
import CustomPrompt from "./app/app/chat-settings/custom-prompt.tsx";
import RefundRequests from "./app/app/refund-requests/refund-requests.tsx";
import RequestDetails from "./app/app/refund-requests/request-details.tsx";
import AccountLayout from "./components/account-settings/account-layout.tsx";
import Members from "./app/app/account/members.tsx";
import Subscriptions from "./app/app/account/subscriptions.tsx";
import ProactiveChatList from "./components/proactive-chats/proactive-chat-list/proactive-chat-list.tsx";
import ManagePermissions from "./app/app/account/manage-permissions.tsx";
import ShoeSizeRefine from "./app/app/ai-training/shoe-size-refine.tsx";
import * as Sentry from "@sentry/react";
import StoreManagement from "./app/app/store-management/store-management.tsx";
import StoreDetails from "./app/app/store-management/store-details.tsx";
import BillingManage from "./app/app/billing/billing-manage.tsx";
import PackageManage from "./app/app/packages/package-manage.tsx";
import TicketManagement from "./app/app/tickets/ticket-management.tsx";
import StoreAccessManage from "./app/app/store-access/store-access-manage.tsx";
import AccessControl from "./app/app/access-control/access-control.tsx";
import AdminReports from "./app/app/admin-reports/admin-reports.tsx";
import BillingDetails from "./app/app/billing/billing-details.tsx";
import TicketDetails from "./app/app/tickets/ticket-details.tsx";
import PackageDetailsPage from "./app/app/packages/package-details-page.tsx";
import PaymentSuccess from "./app/app/payment/success.tsx";
import PaymentFailed from "./app/app/payment/failed.tsx";
import PaymentCanceled from "./app/app/payment/canceled.tsx";
import Cookies from "js-cookie";

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
});

// Component to manage token storage at the app root
function TokenManager() {
    const { isAuthenticated, getToken } = useKindeAuth();

    useEffect(() => {
        const storeAccessToken = async () => {
            if (isAuthenticated && getToken) {
                try {
                    const token = await getToken();
                    if (token) {
                        Cookies.set(import.meta.env.VITE_KINDE_ACCESS_TOKEN, token, { expires: 7, secure: true, sameSite: "strict" });
                    }
                } catch (error) {
                    console.error("Error fetching token:", error);
                }
            }
        };

        storeAccessToken();
    }, [isAuthenticated, getToken]);

    return null;
}

const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <KindeProvider
            clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
            domain={import.meta.env.VITE_KINDE_DOMAIN}
            logoutUri={window.location.origin}
            redirectUri={window.location.origin}
            isDangerouslyUseLocalStorage={true}
        >
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <TokenManager />
                    <Routes>
                        {/* landing page */}
                        <Route path="/" element={<App />} />

                        {/* Nested Auth Layout to Check Authentication */}
                        <Route path="auth" element={<AuthLayout />}>
                            <Route index element={<AuthLayout />} />
                            <Route path="sign-in" element={<SignIn />} />
                            <Route path="sign-up" element={<SignUp />} />
                            <Route path="*" element={<AuthRest />} />
                        </Route>

                        {/* Onboarding layout to check authentication */}
                        <Route path="onboarding" element={<OnboardingLayout />}>
                            <Route index element={<OnboardingRest />} />
                            <Route path="step-1" element={<StepOne />} />
                            <Route path="step-2" element={<StepTwo />} />
                            <Route path="step-3" element={<StepThree />} />
                            <Route path="*" element={<OnboardingRest />} />
                        </Route>

                        {/* Main App Layout */}
                        <Route path="app" element={<AppLayout />}>
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="store-management">
                                <Route index element={<StoreManagement />} />
                                <Route path="store/:id" element={<StoreDetails />} />
                            </Route>
                            <Route path="billing">
                                <Route index element={<BillingManage />} />
                                <Route path=":id" element={<BillingDetails />} />
                            </Route>
                            <Route path="packages">
                                <Route index element={<PackageManage />} />
                                <Route path=":id" element={<PackageDetailsPage />} />
                            </Route>
                            <Route path="tickets">
                                <Route index element={<TicketManagement />} />
                                <Route path=":id" element={<TicketDetails />} />
                            </Route>
                            <Route path="store-access">
                                <Route index element={<StoreAccessManage />} />
                            </Route>
                            <Route path="access-control">
                                <Route index element={<AccessControl />} />
                            </Route>
                            <Route path="admin-reports">
                                <Route index element={<AdminReports />} />
                            </Route>
                            <Route path="conversations" element={<Conversations />} />
                            <Route path="leads" element={<Leads />} />
                            <Route path="proactive-chats" element={<ProactiveLayout />}>
                                <Route path="" index element={<Navigate to={"proactive-chat-list"} />} />
                                <Route path="easy-setup" element={<EasySetup />} />
                                <Route path="advance-setup" element={<AdvancedSetup />} />
                                <Route path="proactive-chat-list" element={<ProactiveChatList />} />
                            </Route>
                            <Route path="ai-training" element={<TrainingLayout />}>
                                <Route path="" index element={<Navigate to={"knowledge-base"} />} />
                                <Route path="knowledge-base" element={<KnowledgeBase />} />
                                <Route path="active-trainings" element={<ActiveTrainings />} />
                                <Route path="training-history" element={<TrainingHistory />} />
                                <Route path="corrections" element={<Corrections />} />
                                <Route path="custom-instructions" element={<CustomInstructions />} />
                                <Route path="shoe-size-refine" element={<ShoeSizeRefine />} />
                            </Route>
                            <Route path="chat-settings" element={<ChatSettingsLayout />}>
                                <Route path="" index element={<Navigate to={"look-and-feel"} />} />
                                <Route path="look-and-feel" element={<LookAndFeel />} />
                                <Route path="predefined-questions" element={<PredefinedQuestions />} />
                                <Route path="configuration" element={<Configuration />} />
                                <Route path="integrations" element={<Integration />} />
                                <Route path="installation" element={<Installation />} />
                                <Route path="custom-prompt" element={<CustomPrompt />} />
                            </Route>

                            <Route path="refund-request">
                                <Route index element={<RefundRequests />} />
                                <Route path="details/:request_id" element={<RequestDetails />} />
                            </Route>

                            <Route path="account-settings" element={<AccountLayout />}>
                                <Route path="" index element={<Navigate to="members" />} />
                                <Route path="members" element={<Members />} />
                                <Route path="subscriptions" element={<Subscriptions />} />
                                <Route path="permission-manage" element={<ManagePermissions />} />
                            </Route>

                            <Route path="payment">
                                <Route path="success" element={<PaymentSuccess />} />
                                <Route path="failed" element={<PaymentFailed />} />
                                <Route path="canceled" element={<PaymentCanceled />} />
                            </Route>

                            <Route path="*" element={<>Page not ready yet</>} />
                        </Route>
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors closeButton />
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </KindeProvider>
    </StrictMode>
);
